import * as Yup from "yup";
import Box from "@material-ui/core/Box";
import CircularProgress from "@material-ui/core/CircularProgress";
import LockIcon from "@material-ui/icons/Lock";
import MuiButton from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import Typography from "@material-ui/core/Typography";
import React, { ChangeEvent, FC, useState } from "react";
import { Auth } from "aws-amplify";
import { Field, Form, Formik, FormikHelpers } from "formik";
import { Theme } from "@material-ui/core/styles";
import { styled, useTheme } from "@material-ui/styles";
import { useHistory } from "react-router-dom";

import AppLink from "../../../../components/elements/AppLink";
import FormFieldError from "../../../../components/elements/FormFieldError";
import PasswordField from "../../../../components/redesign/elements/PasswordField";
import { successToaster } from "../../../../helpers/toasters";

const FormColumn = styled("div")({
  display: "flex",
  flexDirection: "column",
  gap: 12,
});

const FieldGroup = styled("div")({
  display: "flex",
  flexDirection: "column",
  gap: 4,
});

const FieldLabel = styled("div")(({ theme }: { theme: Theme }) => ({
  fontSize: 10,
  fontWeight: 700,
  color: theme.palette.brand.gray500,
  letterSpacing: "0.05em",
  textTransform: "uppercase" as const,
}));

const RedesignedTextField = styled(TextField)(({ theme }: { theme: Theme }) => ({
  "& .MuiOutlinedInput-root": {
    "& fieldset": {
      border: `1px solid ${theme.palette.brand.gray200}`,
      borderRadius: 8,
    },
    "&.Mui-focused fieldset": {
      border: `1px solid ${theme.palette.brand.blueAccent}`,
    },
  },
}));

const CoralButton = styled(MuiButton)(({ theme }: { theme: Theme }) => ({
  backgroundColor: theme.palette.brand.coral,
  color: theme.palette.brand.white,
  borderRadius: 8,
  fontSize: 16,
  fontWeight: 600,
  height: 48,
  textTransform: "none",
  "&:hover": {
    backgroundColor: theme.palette.brand.coralHover,
  },
  "&:disabled": {
    backgroundColor: theme.palette.brand.coral,
    opacity: 0.7,
  },
}));

type FormInitialValues = {
  confirmationCode: string;
  newPassword: string;
  newPasswordConfirm: string;
};

type RedesignedNewPasswordFormProps = {
  userName: string;
};

const validationSchema = Yup.object({
  confirmationCode: Yup.string().required("Required field."),
  newPassword: Yup.string().required("Required field.").min(8, "Password is too short - should be 8 chars minimum."),
  newPasswordConfirm: Yup.string().oneOf([Yup.ref("newPassword"), null], "Passwords must match"),
});

const RedesignedNewPasswordForm: FC<RedesignedNewPasswordFormProps> = ({ userName }) => {
  const history = useHistory();
  const theme = useTheme<Theme>();
  const [errorMessage, setErrorMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const formInitialValues: FormInitialValues = {
    confirmationCode: "",
    newPassword: "",
    newPasswordConfirm: "",
  };

  const handleSubmit = async (values: FormInitialValues, actions: FormikHelpers<FormInitialValues>) => {
    try {
      const { confirmationCode, newPassword } = values;
      setIsLoading(true);
      await Auth.forgotPasswordSubmit(userName, confirmationCode, newPassword);
      setIsLoading(false);
      actions.resetForm();
      successToaster("Password successfully changed!");
      history.push("/");
    } catch (error) {
      const err = error as any;
      setIsLoading(false);
      actions.resetForm();
      setErrorMessage(`There was an error: ${err.message}`);
    }
  };

  const handleFieldChange =
    (formikHandler: (e: string | ChangeEvent<any>) => void) => (e: string | ChangeEvent<any>) => {
      formikHandler(e);
      if (errorMessage) setErrorMessage("");
    };

  return (
    <Formik
      initialValues={formInitialValues}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
      validateOnBlur={false}
    >
      {({ handleChange, values }) => (
        <Form>
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={3}>
            <Typography style={{ fontSize: 24, fontWeight: 700, color: theme.palette.brand.gray900 }}>
              New Password
            </Typography>
            <LockIcon style={{ fontSize: 40, color: theme.palette.brand.gray900 }} />
          </Box>

          <Typography style={{ fontSize: 13, color: theme.palette.brand.gray500, marginBottom: 20 }}>
            Enter the confirmation code sent to your email and choose a new password.
          </Typography>

          <FormColumn>
            <FieldGroup>
              <FieldLabel>Confirmation Code</FieldLabel>
              <Field
                as={RedesignedTextField}
                value={values.confirmationCode}
                error={!!errorMessage}
                name="confirmationCode"
                variant="outlined"
                fullWidth
                onChange={handleFieldChange(handleChange("confirmationCode"))}
              />
              <Box style={{ minHeight: 30 }}>
                <FormFieldError name="confirmationCode" />
              </Box>
            </FieldGroup>

            <FieldGroup>
              <FieldLabel>New Password</FieldLabel>
              <PasswordField
                value={values.newPassword}
                error={!!errorMessage}
                name="newPassword"
                label=""
                onChange={handleFieldChange(handleChange("newPassword"))}
              />
              <Box style={{ minHeight: 30 }}>
                <FormFieldError name="newPassword" />
              </Box>
            </FieldGroup>

            <FieldGroup>
              <FieldLabel>Confirm New Password</FieldLabel>
              <PasswordField
                value={values.newPasswordConfirm}
                error={!!errorMessage}
                name="newPasswordConfirm"
                label=""
                onChange={handleFieldChange(handleChange("newPasswordConfirm"))}
              />
              <Box style={{ minHeight: 30 }}>
                <FormFieldError name="newPasswordConfirm" />
                {errorMessage && (
                  <Typography style={{ fontSize: 13, color: theme.palette.brand.errorRed }}>{errorMessage}</Typography>
                )}
              </Box>
            </FieldGroup>
          </FormColumn>

          <CoralButton variant="contained" type="submit" fullWidth disabled={isLoading} disableElevation>
            {isLoading ? <CircularProgress size={22} style={{ color: theme.palette.brand.white }} /> : "Reset Password"}
          </CoralButton>

          <Box display="flex" justifyContent="center" mt={2}>
            <AppLink highlighted to="/request-password-reset">
              Cancel
            </AppLink>
          </Box>
        </Form>
      )}
    </Formik>
  );
};

export default RedesignedNewPasswordForm;
